import { useState, useEffect, useCallback } from 'preact/hooks';
import { t } from '../lib/i18n';
import { Wallet, Check, Loader2, ShieldCheck, Link2Off } from 'lucide-preact';

// §C payment-BYOK — the creator connects their OWN payment processor. The secret
// is stored encrypted in the Vault (server-side); generated apps route end-user
// payments straight to the creator. Pomela never holds funds and takes no cut —
// the panel says so plainly. Reads GET /v1/payments; PUT connect / DELETE disconnect.

interface Conn { connected: boolean; processor: string | null }
type Phase = { k: 'loading' } | { k: 'ready' } | { k: 'saving' } | { k: 'error'; msg: string };

const PROCESSORS = [
  { id: 'stripe', label: 'Stripe', hint: 'sk_live_… or sk_test_…' },
  { id: 'paypal', label: 'PayPal', hint: 'Client secret' }
  // Only Stripe + PayPal have registered adapters (lib/payment/adapters). Others
  // 400 `unsupported_processor` at connect — do NOT list them until adapters ship.
];

export function PaymentConnect() {
  const [conn, setConn] = useState<Conn | null>(null);
  const [phase, setPhase] = useState<Phase>({ k: 'loading' });
  const [processor, setProcessor] = useState('stripe');
  const [secret, setSecret] = useState('');
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/v1/payments', { credentials: 'include' });
      if (!res.ok) { setPhase({ k: 'error', msg: t('pc.errSignIn') }); return; }
      setConn((await res.json()) as Conn);
      setPhase({ k: 'ready' });
    } catch { setPhase({ k: 'error', msg: t('pc.errLoad') }); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const connect = async () => {
    if (secret.trim().length < 8) return;
    setPhase({ k: 'saving' });
    try {
      const res = await fetch('/v1/payments/connect', { method: 'PUT', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ processor, secretKey: secret.trim() }) });
      if (!res.ok) { setPhase({ k: 'error', msg: t('pc.errRejected') }); return; }
      setSecret(''); setOpen(false); await load();
    } catch { setPhase({ k: 'error', msg: t('pc.errConnect') }); }
  };
  const disconnect = async () => {
    setPhase({ k: 'saving' });
    try { await fetch('/v1/payments/disconnect', { method: 'DELETE', credentials: 'include' }); await load(); }
    catch { setPhase({ k: 'error', msg: t('pc.errDisconnect') }); }
  };

  const label = PROCESSORS.find((p) => p.id === conn?.processor)?.label ?? conn?.processor;
  const hint = PROCESSORS.find((p) => p.id === processor)?.hint ?? '';

  return (
    <section data-testid="payment-connect" className="surface flex flex-col gap-4 p-5 animate-vs-fade-in" aria-label={t('pc.aria')}>
      <header className="flex items-center gap-2.5">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[color:var(--color-primary)]/12 text-[color:var(--color-primary)]"><Wallet size={14} strokeWidth={2.2} /></span>
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold tracking-tight text-[color:var(--fg)]">{t('pc.title')}</h2>
          <p className="text-[12px] leading-tight text-[color:var(--fg-muted)]">{t('pc.sub')}</p>
        </div>
      </header>

      {phase.k === 'loading' && <div className="h-16 rounded-[var(--radius-md)] bg-[color:var(--bg-subtle)] animate-pulse" aria-busy="true" />}
      {phase.k === 'error' && <p className="rounded-[var(--radius-md)] border border-[color:var(--border)] bg-[color:var(--bg-subtle)] px-3 py-2.5 text-[13px] text-[color:var(--fg-muted)]">{phase.msg}</p>}

      {conn && phase.k !== 'loading' && phase.k !== 'error' && (
        <>
          {conn.connected ? (
            <div className="flex items-center justify-between rounded-[var(--radius-lg)] border border-[color:var(--color-success,#10B981)]/30 bg-[color:var(--color-success,#10B981)]/[0.07] px-4 py-3">
              <div className="flex items-center gap-2.5">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--color-success,#10B981)]/15 text-[color:var(--color-success,#0F9D6E)]"><Check size={16} strokeWidth={2.5} /></span>
                <div>
                  <p className="text-[13px] font-semibold text-[color:var(--fg)] capitalize">{t('pc.connected', { p: label ?? '' })}</p>
                  <p className="text-[12px] text-[color:var(--fg-muted)]">{t('pc.connectedSub', { p: label ?? '' })}</p>
                </div>
              </div>
              <button type="button" onClick={disconnect} disabled={phase.k === 'saving'} className="inline-flex h-9 items-center gap-1 rounded-[var(--radius-md)] px-2.5 text-[12px] font-medium text-[color:var(--fg-subtle)] transition hover:text-[color:var(--color-danger)] disabled:opacity-50"><Link2Off size={13} /> {t('pc.disconnect')}</button>
            </div>
          ) : !open ? (
            <button type="button" onClick={() => setOpen(true)} className="inline-flex h-11 items-center justify-center gap-1.5 self-start rounded-[var(--radius-md)] bg-[color:var(--color-primary)] px-4 text-[13px] font-semibold text-white shadow-sm transition hover:bg-[color:var(--color-primary-hover)] active:scale-[0.98]"><Wallet size={14} /> {t('pc.connectCta')}</button>
          ) : (
            <div className="flex flex-col gap-3 rounded-[var(--radius-lg)] border border-[color:var(--border)] bg-[color:var(--bg-subtle)] p-3.5 animate-vs-fade-in">
              <div className="flex flex-wrap gap-1.5">
                {PROCESSORS.map((p) => (
                  <button key={p.id} type="button" onClick={() => setProcessor(p.id)} className={`h-8 rounded-full px-3 text-[12px] font-medium transition ${processor === p.id ? 'bg-[color:var(--color-primary)] text-white' : 'border border-[color:var(--border)] text-[color:var(--fg-muted)] hover:text-[color:var(--fg)]'}`}>{p.label}</button>
                ))}
              </div>
              <input
                type="password"
                value={secret}
                onInput={(e) => setSecret((e.target as HTMLInputElement).value)}
                placeholder={hint}
                style={{ outline: 'none', '--tw-ring-color': 'color-mix(in srgb, var(--color-primary) 30%, transparent)' } as Record<string, string>}
                className="min-h-[44px] rounded-[var(--radius-md)] border border-[color:var(--border)] bg-[color:var(--bg-elevated)] px-3 text-[14px] text-[color:var(--fg)] outline-none transition focus:border-[color:var(--color-primary)] focus:ring-2"
                aria-label={t('pc.secretAria')}
              />
              <div className="flex items-center gap-2">
                <button type="button" onClick={connect} disabled={phase.k === 'saving' || secret.trim().length < 8} className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-[var(--radius-md)] bg-[color:var(--color-primary)] px-3 text-[13px] font-semibold text-white shadow-sm transition hover:bg-[color:var(--color-primary-hover)] active:scale-[0.98] disabled:opacity-50">{phase.k === 'saving' ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} {t('pc.connect')}</button>
                <button type="button" onClick={() => { setOpen(false); setSecret(''); }} className="inline-flex h-10 items-center justify-center rounded-[var(--radius-md)] border border-[color:var(--border)] px-3.5 text-[13px] font-medium text-[color:var(--fg)] transition hover:bg-[color:var(--bg-elevated)]">{t('pc.cancel')}</button>
              </div>
              <p className="flex items-center gap-1.5 text-[11px] text-[color:var(--fg-subtle)]"><ShieldCheck size={13} className="shrink-0" /> {t('pc.encrypted')}</p>
            </div>
          )}

          {/* §G — the user-facing explainer: what connecting actually does. */}
          <details className="group rounded-[var(--radius-lg)] border border-[color:var(--border)] bg-[color:var(--bg-subtle)]/60">
            <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between px-3.5 text-[13px] font-medium text-[color:var(--fg-muted)] transition hover:text-[color:var(--fg)] [&::-webkit-details-marker]:hidden">
              {t('pc.how')}
              <span className="text-[color:var(--fg-subtle)] transition-transform group-open:rotate-180">⌄</span>
            </summary>
            <ol className="flex flex-col gap-2.5 px-3.5 pb-3.5 text-[12px] leading-relaxed text-[color:var(--fg-muted)]">
              <li dangerouslySetInnerHTML={{ __html: t('pc.how1') }} />
              <li dangerouslySetInnerHTML={{ __html: t('pc.how2') }} />
              <li dangerouslySetInnerHTML={{ __html: t('pc.how3') }} />
              <li className="text-[11px] text-[color:var(--fg-subtle)]">{t('pc.howReq')}</li>
            </ol>
          </details>
        </>
      )}
    </section>
  );
}
